import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Utensils, AlertCircle, CheckCircle, Edit, Trash2, Search, X, Check, ArrowUpDown, Folder, MoreVertical, Copy } from 'lucide-react';
import RecipeCategoryForm from './RecipeCategoryForm.jsx';
import RecipeDetails from './RecipeDetails.jsx';
import RecipeForm from './RecipeForm.jsx';
import AllergenBadge from '../Allergens/AllergenBadge.jsx';
import { recipesAPI, uploadAPI } from '../../utils/api';
import { formatRecipeSaveError } from '../../utils/recipeSaveErrors.js';
import { useCurrency } from '../../contexts/CurrencyContext';
import { useTheme } from '../../contexts/ThemeContext';

  const RecipeCategories = () => {
    const { t, i18n } = useTranslation();
  const { formatCurrency } = useCurrency();
  const navigate = useNavigate();
  const { theme, isDark } = useTheme();
  const [categories, setCategories] = useState([]);
  const [allRecipes, setAllRecipes] = useState([]);
  const [showCategoryForm, setShowCategoryForm] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [showRecipeDeleteModal, setShowRecipeDeleteModal] = useState(false);
  const [recipeToDelete, setRecipeToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchMode, setSearchMode] = useState('categories'); // 'categories' | 'recipes'
  const [selectedRecipes, setSelectedRecipes] = useState(new Set());
  const [isBulkDeleteMode, setIsBulkDeleteMode] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);
  const [recipeSortOrder, setRecipeSortOrder] = useState('id'); // 'id' | 'alphabetical'
  const [categorySortOrder, setCategorySortOrder] = useState('id'); // 'id' | 'alphabetical'
  const [showRecipeDetails, setShowRecipeDetails] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showRecipeForm, setShowRecipeForm] = useState(false);
  // Category bulk delete state
  const [isCategoryBulkDeleteMode, setIsCategoryBulkDeleteMode] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState(new Set());
  const [showCategoryBulkDeleteConfirm, setShowCategoryBulkDeleteConfirm] = useState(false);
  
  // Dropdown state
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [duplicatingRecipeId, setDuplicatingRecipeId] = useState(null);
  const [openCategoryDropdownId, setOpenCategoryDropdownId] = useState(null);


  useEffect(() => {
    loadData();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenDropdownId(null);
    };

    if (openDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openDropdownId]);

  // Close category dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      setOpenCategoryDropdownId(null);
    };

    if (openCategoryDropdownId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openCategoryDropdownId]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [categoriesData, recipesData] = await Promise.all([
        recipesAPI.getCategories(),
        recipesAPI.getAll(),
      ]);
      
      // Sort categories to put default category first
      const sortedCategories = categoriesData.sort((a, b) => {
        // Check if either category is the default category
        const aIsDefault = a.id === 'default' || a.name_en?.toLowerCase().includes('default') || a.name_en?.toLowerCase().includes('unnamed');
        const bIsDefault = b.id === 'default' || b.name_en?.toLowerCase().includes('default') || b.name_en?.toLowerCase().includes('unnamed');
        
        if (aIsDefault && !bIsDefault) return -1; // a comes first
        if (!aIsDefault && bIsDefault) return 1;  // b comes first
        return 0; // both are default or both are not default, maintain original order
      });
      
      setCategories(sortedCategories);
      setAllRecipes(recipesData);
    } catch (error) {
      console.error('Error loading categories:', error);
      setErrorMessage('Failed to load recipe categories');
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      if (categoryData.id) {
        // Update existing category
        const updatedCategory = await recipesAPI.updateCategory(categoryData.id, categoryData);
        
        // Preserve the recipe_count from the existing category if not provided in the response
        const existingCategory = categories.find(cat => cat.id === categoryData.id);
        const categoryWithCount = {
          ...updatedCategory,
          recipe_count: updatedCategory.recipe_count ?? existingCategory?.recipe_count ?? 0
        };
        
        setCategories(categories.map(category => 
          category.id === categoryData.id ? categoryWithCount : category
        ));
        setSuccessMessage(t('messages.categorySaved'));
      } else {
        // Add new category
        const newCategory = await recipesAPI.createCategory(categoryData);
        setCategories([...categories, newCategory]);
        setSuccessMessage(t('messages.categorySaved'));
      }
      
      setShowCategoryForm(false);
      setEditingCategory(null);
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error saving category:', error);
      setErrorMessage('Failed to save category');
      setShowErrorModal(true);
    }
  };

  const handleCategoryClick = (categoryId) => {
            navigate(`/dish-costing/recipes/category/${categoryId}`);
  };

  const handleEditCategory = (category, event) => {
    event.stopPropagation(); // Prevent navigation when clicking edit
    setEditingCategory(category);
    setShowCategoryForm(true);
  };

  const handleDeleteCategory = (category, event) => {
    event.stopPropagation(); // Prevent navigation when clicking delete
    
    // Check if category has recipes
    if (category.recipe_count > 0) {
      setErrorMessage(t('messages.cannotDeleteCategoryContainsRecipes', { name: category.name_en, count: category.recipe_count }));
      setShowErrorModal(true);
      return;
    }
    
    setCategoryToDelete(category);
    setShowDeleteModal(true);
  };

  // Recipes mode actions
  const handleRecipeCardClick = (recipe) => {
    if (isBulkDeleteMode) return;
    setSelectedRecipe(recipe);
    setShowRecipeDetails(true);
  };

  const handleEditRecipe = async (recipe) => {
    try {
      // Fetch the complete recipe data including ingredients
      const fullRecipe = await recipesAPI.getById(recipe.id);
      setSelectedRecipe(fullRecipe);
      setShowRecipeForm(true);
    } catch (error) {
      console.error('Error fetching recipe details:', error);
      setErrorMessage('Failed to load recipe details');
      setShowErrorModal(true);
    }
  };

  const handleDeleteRecipe = (recipe) => {
    setRecipeToDelete(recipe);
    setShowRecipeDeleteModal(true);
  };

  const handleDuplicateRecipe = async (recipe) => {
    if (duplicatingRecipeId) return;
    try {
      setDuplicatingRecipeId(recipe.id);
      setOpenDropdownId(null);
      await recipesAPI.duplicate(recipe.id);
      await loadData();
      setSuccessMessage(t('recipes.recipeDuplicatedSuccessfully'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error duplicating recipe:', error);
      setErrorMessage(t('recipes.failedToDuplicateRecipe'));
      setShowErrorModal(true);
    } finally {
      setDuplicatingRecipeId(null);
    }
  };

  const handleSelectRecipe = (recipeId) => {
    const next = new Set(selectedRecipes);
    if (next.has(recipeId)) next.delete(recipeId);
    else next.add(recipeId);
    setSelectedRecipes(next);
  };

  const handleSelectAllRecipes = () => {
    const current = filteredAllRecipes;
    if (selectedRecipes.size === current.length) {
      setSelectedRecipes(new Set());
    } else {
      setSelectedRecipes(new Set(current.map(r => r.id)));
    }
  };

  const enterBulkDeleteMode = () => {
    setIsBulkDeleteMode(true);
    setSelectedRecipes(new Set());
  };

  const cancelBulkDeleteMode = () => {
    setIsBulkDeleteMode(false);
    setSelectedRecipes(new Set());
  };

  // Function to extract public ID from Cloudinary URL
  const extractPublicIdFromUrl = (url) => {
    if (!url) return null;
    try {
      // Extract public ID from Cloudinary URL
      // Format: https://res.cloudinary.com/cloud_name/image/upload/v123456789/folder/image.jpg
      const match = url.match(/\/upload\/[^\/]+\/(.+)$/);
      if (match) {
        let publicId = match[1];
        // Remove file extension from public ID (Cloudinary public IDs don't include extensions)
        publicId = publicId.replace(/\.[^/.]+$/, '');
        return publicId;
      }
      return null;
    } catch (error) {
      console.error('Error extracting public ID from URL:', error);
      return null;
    }
  };

  // Function to clean up recipe image from Cloudinary
  const cleanupRecipeImage = async (imageUrl) => {
    if (!imageUrl) return;
    try {
      const imagePublicId = extractPublicIdFromUrl(imageUrl);
      if (imagePublicId) {
        console.log('🧹 Cleanup: Deleting recipe image:', imagePublicId);
        await uploadAPI.deleteImage(imagePublicId);
        console.log('✅ Cleanup: Recipe image cleaned up successfully');
      }
    } catch (error) {
      console.error('❌ Cleanup: Failed to cleanup recipe image:', error);
    }
  };

  // Function to clean up procedure step images from Cloudinary
  const cleanupProcedureImages = async (procedures) => {
    if (!procedures || !Array.isArray(procedures)) return;
    
    for (const step of procedures) {
      if (step.image_url) {
        try {
          const imagePublicId = extractPublicIdFromUrl(step.image_url);
          if (imagePublicId) {
            console.log('🧹 Cleanup: Deleting procedure image:', imagePublicId);
            await uploadAPI.deleteImage(imagePublicId);
            console.log('✅ Cleanup: Procedure image cleaned up successfully');
          }
        } catch (error) {
          console.error('❌ Cleanup: Failed to cleanup procedure image:', error);
        }
      }
    }
  };

  const handleBulkDeleteRecipes = async () => {
    const ids = Array.from(selectedRecipes);
    setConfirmAction(() => async () => {
      try {
        // Fetch full recipe data (including procedures) for all recipes to be deleted
        console.log('🔍 Fetching full recipe data for bulk cleanup before deletion...');
        const fullRecipesData = await Promise.allSettled(
          ids.map(id => recipesAPI.getById(id))
        );
        
        const results = await Promise.allSettled(ids.map(id => recipesAPI.delete(id)));
        const successful = results.filter(r => r.status === 'fulfilled').length;
        const failed = results.filter(r => r.status === 'rejected');
        
        // Get recipe names for failed deletions
        const failedRecipeNames = [];
        const failedRecipeIds = [];
        failed.forEach((result, index) => {
          const recipeId = ids[index];
          const recipe = allRecipes.find(r => r.id === recipeId);
          if (recipe) {
            failedRecipeNames.push(recipe.name_en);
            failedRecipeIds.push(recipeId);
          }
        });
        
        if (successful > 0) {
          // Reload all recipes to ensure dependent recipes are updated
          // This is necessary because deleting recipes can affect other recipes that use them
          await loadData();
          
          let successMessage = t('messages.bulkRecipesDeleted', { count: successful });
          
          if (failed.length > 0) {
            successMessage += `\n\n${failed.length} recipe${failed.length !== 1 ? 's' : ''} could not be deleted:`;
            failedRecipeNames.forEach((name, index) => {
              const reason = failed[index].reason?.message?.includes('Cannot delete recipe that is being used as ingredient') 
                ? ' (used as ingredient in other recipes)' 
                : ' (unknown error)';
              successMessage += `\n• ${name}${reason}`;
            });
          }
          
          successMessage += `\n\n${t('recipes.allDependentRecipesRecalculated')}`;
          setSuccessMessage(successMessage);
          setShowSuccessModal(true);
          
          // Clean up images for successfully deleted recipes in the background
          fullRecipesData.forEach((fullRecipeResult, index) => {
            if (fullRecipeResult.status === 'fulfilled' && results[index].status === 'fulfilled') {
              const fullRecipe = fullRecipeResult.value;
              console.log('🧹 Cleaning up images for recipe:', fullRecipe.id);
              
              // Clean up main recipe image
              if (fullRecipe.image_url) {
                cleanupRecipeImage(fullRecipe.image_url).catch(error => {
                  console.error('Background cleanup failed for recipe image:', error);
                });
              }
              
              // Clean up procedure step images
              if (fullRecipe.procedures && Array.isArray(fullRecipe.procedures)) {
                cleanupProcedureImages(fullRecipe.procedures).catch(error => {
                  console.error('Background cleanup failed for procedure images:', error);
                });
              }
            }
          });
        } else if (failed.length > 0) {
          // No recipes were deleted, but some failed
          let errorMessage = `No recipes could be deleted. ${failed.length} recipe${failed.length !== 1 ? 's' : ''} failed:`;
          failedRecipeNames.forEach((name, index) => {
            const reason = failed[index].reason?.message?.includes('Cannot delete recipe that is being used as ingredient') 
              ? ' (used as ingredient in other recipes)' 
              : ' (unknown error)';
            errorMessage += `\n• ${name}${reason}`;
          });
          setErrorMessage(errorMessage);
          setShowErrorModal(true);
        }
        setSelectedRecipes(new Set());
        setIsBulkDeleteMode(false);
      } catch (error) {
        console.error('Bulk delete recipes error:', error);
        setErrorMessage(t('messages.bulkDeleteUnexpected'));
        setShowErrorModal(true);
      }
    });
    setShowBulkDeleteConfirm(true);
  };

  const handleSaveRecipe = async (recipeData) => {
    try {
      if (selectedRecipe && selectedRecipe.id) {
        // Update existing recipe
        const updatedRecipe = await recipesAPI.update(selectedRecipe.id, recipeData);
        
        // Reload all recipes to ensure dependent recipes are updated
        // This is necessary because changing servings/ingredients can affect other recipes
        await loadData();
        
        setSuccessMessage(t('recipes.recipeUpdatedSuccessfully'));
      } else {
        // Create new recipe
        const newRecipe = await recipesAPI.create(recipeData);
        
        // Reload all recipes to ensure the new recipe is properly calculated
        await loadData();
        
        setSuccessMessage(t('recipes.recipeCreatedSuccessfully'));
      }
      setShowSuccessModal(true);
      setShowRecipeForm(false);
      setSelectedRecipe(null);
    } catch (error) {
      console.error('Error saving recipe:', error);
      setErrorMessage(formatRecipeSaveError(error, t));
      setShowErrorModal(true);
    }
  };

  // Category bulk delete handlers
  const enterCategoryBulkDeleteMode = () => {
    setIsCategoryBulkDeleteMode(true);
    setSelectedCategories(new Set());
  };

  const cancelCategoryBulkDeleteMode = () => {
    setIsCategoryBulkDeleteMode(false);
    setSelectedCategories(new Set());
  };

  const toggleCategorySelection = (categoryId) => {
    // Prevent selection of default category (ID 0)
    if (categoryId === 0) {
      setErrorMessage("Default category cannot be deleted as it is essential for the system.");
      setShowErrorModal(true);
      return;
    }
    
    // Check if category contains recipes
    const category = categories.find(cat => cat.id === categoryId);
    if (category && Number(category.recipe_count || 0) > 0) {
      setErrorMessage(t('messages.cannotDeleteCategoryContainsRecipes', { name: category.name_en, count: category.recipe_count }));
      setShowErrorModal(true);
      return;
    }
    
    const next = new Set(selectedCategories);
    if (next.has(categoryId)) {
      next.delete(categoryId);
    } else {
      next.add(categoryId);
    }
    setSelectedCategories(next);
  };

  const handleSelectAllCategories = () => {
    const selectable = filteredCategories.filter(c => c.id !== 0 && Number(c.recipe_count || 0) === 0);
    
    if (selectedCategories.size === selectable.length) {
      setSelectedCategories(new Set());
    } else {
      setSelectedCategories(new Set(selectable.map(c => c.id)));
    }
  };

  const handleBulkDeleteCategories = async () => {
    const ids = Array.from(selectedCategories);
    setConfirmAction(() => async () => {
      try {
        const results = await Promise.allSettled(ids.map(id => recipesAPI.deleteCategory(id)));
        const successful = [];
        const failed = [];
        results.forEach((r, idx) => {
          const id = ids[idx];
          if (r.status === 'fulfilled') successful.push(id);
          else failed.push(id);
        });
        if (successful.length > 0) {
          setCategories(prev => prev.filter(c => !successful.includes(c.id)));
          setSuccessMessage(t('messages.bulkCategoriesDeleted', { count: successful.length }));
          setShowSuccessModal(true);
        }
        if (failed.length > 0) {
          setErrorMessage(t('messages.bulkCategoriesOtherErrors', { count: failed.length }));
          setShowErrorModal(true);
        }
        setSelectedCategories(new Set());
        setIsCategoryBulkDeleteMode(false);
      } catch (error) {
        console.error('Bulk delete categories error:', error);
        setErrorMessage(t('messages.bulkDeleteUnexpected'));
        setShowErrorModal(true);
      }
    });
    setShowCategoryBulkDeleteConfirm(true);
  };

  const confirmDeleteCategory = async () => {
    if (!categoryToDelete) return;

    try {
      await recipesAPI.deleteCategory(categoryToDelete.id);
      setCategories(categories.filter(category => category.id !== categoryToDelete.id));
      setSuccessMessage(t('messages.categoryDeleted'));
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error deleting category:', error);
      if (error.message && error.message.includes('Cannot delete category that is being used in recipes')) {
        setErrorMessage(t('messages.cannotDeleteCategoryUsedInRecipes'));
      } else {
        setErrorMessage(t('messages.failedDeleteCategory'));
      }
      setShowErrorModal(true);
    } finally {
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const filteredCategories = categories
    .filter(category => 
    category.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.id?.toString().includes(searchTerm)
    )
    .sort((a, b) => {
      if (categorySortOrder === 'id') return (a.id || 0) - (b.id || 0);
      return (a.name_en || '').localeCompare(b.name_en || '');
    });

  const filteredAllRecipes = allRecipes
    .filter(recipe => 
      recipe.name_en?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.name_ar?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      recipe.id?.toString().includes(searchTerm)
    )
    .sort((a, b) => {
      if (recipeSortOrder === 'id') return (a.id || 0) - (b.id || 0);
      return (a.name_en || '').localeCompare(b.name_en || '');
    });

  if (loading) {
    return (
      <div className="bg-background text-foreground min-h-screen p-6">
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">{t('common.loading')} {t('categories.title').toLowerCase()}...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-background text-foreground min-h-screen p-6">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('recipes.title')}</h1>
            <p className="mt-2 text-muted-foreground">
              {t('recipes.subtitle')}
            </p>
          </div>
          
          {/* Action Buttons */}
          <div className={`flex justify-end mt-4 sm:mt-0 ${i18n.language === 'ar' ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
            {searchMode === 'categories' ? (
              isCategoryBulkDeleteMode ? (
                <>
                  <button onClick={cancelCategoryBulkDeleteMode} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-secondary hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">
                    <X className="h-4 w-4 mr-2" />
                    {t('common.cancel')}
                  </button>
                  <button 
                    onClick={handleSelectAllCategories}
                    className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-secondary hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
                  >
                    <Check className="h-4 w-4 mr-2" />
                    {selectedCategories.size > 0 && selectedCategories.size === filteredCategories.filter(c => c.id !== 0 && Number(c.recipe_count || 0) === 0).length ? t('common.deselectAll') : t('common.selectAll')}
                  </button>
                  <button onClick={handleBulkDeleteCategories} disabled={selectedCategories.size === 0} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('common.deleteSelected')} ({selectedCategories.size})
                  </button>
                </>
              ) : (
                <>
                  <button onClick={() => setShowCategoryForm(true)} className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring" title="Add New Category">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('categories.addNew')}
                  </button>
                  <button onClick={enterCategoryBulkDeleteMode} className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring" title={t('common.bulkDelete')}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                </>
              )
            ) : isBulkDeleteMode ? (
              <>
                <button onClick={cancelBulkDeleteMode} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-secondary hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">
                  <X className="h-4 w-4 mr-2" />
                  {t('common.cancel')}
                </button>
                <button onClick={handleSelectAllRecipes} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-secondary hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">
                  <Check className="h-4 w-4 mr-2" />
                  {selectedRecipes.size === filteredAllRecipes.length ? t('common.deselectAll') : t('common.selectAll')}
                </button>
                <button onClick={handleBulkDeleteRecipes} disabled={selectedRecipes.size === 0} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  <Trash2 className="h-4 w-4 mr-2" />
                  {t('common.deleteSelected')} ({selectedRecipes.size})
                </button>
              </>
            ) : (
              <>
                <button onClick={() => setShowRecipeForm(true)} className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring" title="Add New Recipe">
                  <Plus className="h-4 w-4 mr-2" />
                    {t('recipes.addNewDish')}
                </button>
                <button onClick={enterBulkDeleteMode} className="inline-flex items-center justify-center px-3 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 hover:scale-105 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring" title={t('common.bulkDelete')}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>

      {/* Mode Toggle */}
      <div className="flex justify-start">
            <div className="flex bg-muted rounded-lg p-1">
              <button
                onClick={() => { setSearchMode('categories'); setIsBulkDeleteMode(false); setSelectedRecipes(new Set()); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  searchMode === 'categories' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Folder className="h-4 w-4" />
                {t('categories.title')}
              </button>
              <button
                onClick={() => { setSearchMode('recipes'); setIsBulkDeleteMode(false); setSelectedRecipes(new Set()); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                  searchMode === 'recipes' ? 'bg-card text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <Utensils className="h-4 w-4" />
                {t('common.dishes')}
              </button>
        </div>
            </div>

      {/* Search and Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        {/* Search Input */}
        <div className="flex-1 max-w-lg">
          <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <input
                type="text"
              placeholder={searchMode === 'categories' ? t('common.searchByCategoryNameOrId') : t('common.searchByDishNameOrId')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 pl-10 border border-border rounded-lg focus:outline-none focus:border-ring focus:ring-1 focus:ring-ring transition-colors bg-background text-foreground"
                style={{ paddingLeft: '2.75rem' }}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
          </div>
            </div>

        {/* Sort Button */}
              <button
          onClick={(e) => {
            if (searchMode === 'categories') {
              setCategorySortOrder(categorySortOrder === 'id' ? 'alphabetical' : 'id');
            } else {
              setRecipeSortOrder(recipeSortOrder === 'id' ? 'alphabetical' : 'id');
            }
            e.target.blur(); // Remove focus after click
          }}
            className="inline-flex items-center justify-center h-10 px-3 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
          title={`Sort by ${searchMode === 'categories' ? (categorySortOrder === 'id' ? 'alphabetical' : 'ID') : (recipeSortOrder === 'id' ? 'alphabetical' : 'ID')} order`}
              >
                <ArrowUpDown className="h-4 w-4" />
              </button>
        </div>

      {/* Categories Grid */}
      {searchMode === 'categories' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-card-foreground">
              {t('common.allCategories')}
            </h3>
            <div className="text-sm text-muted-foreground">
              {filteredCategories.length} {t('common.categoriesTotal')}
        </div>
      </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
          {filteredCategories.map((category) => (
            <div
              key={category.id}
              onClick={() => {
                if (isCategoryBulkDeleteMode) {
                  toggleCategorySelection(category.id);
                } else {
                  handleCategoryClick(category.id);
                }
              }}
              className={`group ${isCategoryBulkDeleteMode ? 'cursor-default' : 'cursor-pointer'}`}
            >
              <div className={`bg-card rounded-xl border border-border transition-all duration-200 transform flex flex-col h-52 p-6 ${
                isCategoryBulkDeleteMode && selectedCategories.has(category.id)
                  ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                  : 'hover:-translate-y-1'
              }`}>
                {/* Selection Checkbox */}
                {isCategoryBulkDeleteMode && (
                  <div className="absolute top-3 right-3 z-10">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCategorySelection(category.id);
                      }}
                      className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                        category.id === 0 || (category.recipe_count || 0) > 0
                          ? 'bg-muted border-border opacity-50'
                          : selectedCategories.has(category.id)
                          ? 'bg-primary border-primary text-primary-foreground'
                          : 'bg-card border-border hover:border-primary'
                      }`}
                      title={
                        category.id === 0 
                          ? "Default category cannot be deleted" 
                          : (category.recipe_count || 0) > 0
                          ? `Click to see why this category cannot be deleted`
                          : t('common.selectCategoryForDeletion')
                      }
                    >
                      {selectedCategories.has(category.id) && (
                        <Check className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}

                {/* Actions Dropdown */}
                {!isCategoryBulkDeleteMode && (
                  <div className="absolute top-3 right-3 z-10">
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenCategoryDropdownId(openCategoryDropdownId === category.id ? null : category.id);
                        }}
                        className="w-8 h-8 rounded-full border border-border hover:border-border flex items-center justify-center transition-all hover:bg-muted dark:hover:bg-muted bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]"
                      >
                        <MoreVertical className="h-4 w-4 text-muted-foreground" />
                      </button>
                      
                      {openCategoryDropdownId === category.id && (
                        <div className="absolute right-0 top-9 bg-card rounded-lg shadow-lg border border-border py-1 min-w-[120px] z-20">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (category.id !== 0) {
                                handleEditCategory(category, e);
                              }
                              setOpenCategoryDropdownId(null);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                              category.id === 0
                                ? 'text-muted-foreground cursor-not-allowed'
                                : 'text-card-foreground hover:bg-muted'
                            }`}
                            disabled={category.id === 0}
                          >
                            <Edit className="h-4 w-4" />
                            {t('common.edit')}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              if (category.id !== 0) {
                                handleDeleteCategory(category, e);
                              }
                              setOpenCategoryDropdownId(null);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center gap-2 ${
                              category.id === 0
                                ? 'text-muted-foreground cursor-not-allowed'
                                : 'text-destructive hover:bg-muted'
                            }`}
                            disabled={category.id === 0}
                          >
                            <Trash2 className="h-4 w-4" />
                            {t('common.delete')}
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {/* Category ID */}
                <div className="absolute top-3 left-3">
                  <span className="text-xs text-muted-foreground px-2 py-1 rounded-full bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]">
                    {category.id}
                  </span>
                </div>
               
                <div className="space-y-2 flex-1 pt-6">
                  {i18n.language === 'ar' ? (
                    <>
                      {category.name_ar && (
                        <h3 className="font-semibold text-card-foreground text-lg group-hover:text-primary transition-colors truncate font-arabic" title={category.name_ar}>
                          {category.name_ar}
                        </h3>
                      )}
                      <p className="text-sm text-muted-foreground truncate" title={category.name_en}>
                        {category.name_en}
                      </p>
                    </>
                  ) : (
                    <>
                      <h3 className="font-semibold text-card-foreground text-lg group-hover:text-primary transition-colors truncate" title={category.name_en}>
                        {category.name_en}
                      </h3>
                      {category.name_ar && (
                        <p className="text-sm text-muted-foreground font-arabic truncate" title={category.name_ar}>
                          {category.name_ar}
                        </p>
                      )}
                    </>
                  )}
                  
                  {/* Recipe Count */}
                  <div className="text-sm text-muted-foreground mb-2">
                    {category.recipe_count || 0} {t('common.dishes')}
                  </div>
                  
                  
                  <div className="border-t border-border my-2"></div>
                  <div className="flex items-center text-sm text-muted-foreground">
                    <span className="truncate">{t('common.clickToViewDetails')}</span>
                    <svg className="ml-auto h-4 w-4 group-hover:translate-x-1 transition-transform flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        </div>
      )}

      {/* All Recipes Grid (recipes mode) */}
      {searchMode === 'recipes' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-foreground">{t('common.allRecipes')}</h3>
            <div className="text-sm text-muted-foreground">{filteredAllRecipes.length} {t('common.dishes')} {t('common.total')}</div>
          </div>

          {filteredAllRecipes.length === 0 ? (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <Utensils className="h-8 w-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-medium text-foreground mb-2">
                {t('recipes.noRecipesFound')}
              </h3>
              <p className="text-muted-foreground mb-6">
                {t('recipes.noDishesAddedYet')}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 2xl:grid-cols-6 gap-6">
              {filteredAllRecipes.map((recipe) => (
              <div
                key={recipe.id}
                onClick={() => handleRecipeCardClick(recipe)}
                className={`group ${isBulkDeleteMode ? 'cursor-default' : 'cursor-pointer'}`}
              >
                <div className={`bg-card rounded-2xl border border-border transition-all duration-300 transform overflow-hidden flex flex-col h-full ${
                  isBulkDeleteMode && selectedRecipes.has(recipe.id)
                    ? 'border-primary ring-2 ring-primary/20 bg-primary/5'
                    : 'hover:-translate-y-1'
                }`}>
                  {/* Selection Checkbox */}
                  {isBulkDeleteMode && (
                    <div className="absolute top-3 right-3 z-10">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleSelectRecipe(recipe.id);
                        }}
                        className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${
                          selectedRecipes.has(recipe.id)
                            ? 'bg-primary border-primary text-primary-foreground'
                            : 'bg-card border-border hover:border-primary'
                        }`}
                      >
                        {selectedRecipes.has(recipe.id) && (
                          <Check className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  )}

                  {/* Actions Dropdown */}
                  {!isBulkDeleteMode && (
                    <div className="absolute top-3 right-3 z-10">
                      <div className="relative">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdownId(openDropdownId === recipe.id ? null : recipe.id);
                          }}
                          className="w-8 h-8 rounded-full border border-border hover:border-border flex items-center justify-center transition-all hover:bg-muted dark:hover:bg-muted bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]"
                        >
                          <MoreVertical className="h-4 w-4 text-muted-foreground" />
                        </button>
                        
                        {openDropdownId === recipe.id && (
                          <div className="absolute right-0 top-9 bg-card rounded-lg shadow-lg border border-border py-1 min-w-[120px] z-20">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditRecipe(recipe);
                                setOpenDropdownId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-card-foreground hover:bg-muted flex items-center gap-2"
                            >
                              <Edit className="h-4 w-4" />
                              {t('common.edit')}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDuplicateRecipe(recipe);
                              }}
                              disabled={duplicatingRecipeId === recipe.id}
                              className="w-full px-3 py-2 text-left text-sm text-card-foreground hover:bg-muted flex items-center gap-2 disabled:opacity-50"
                            >
                              <Copy className="h-4 w-4" />
                              {t('recipes.duplicate')}
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteRecipe(recipe);
                                setOpenDropdownId(null);
                              }}
                              className="w-full px-3 py-2 text-left text-sm text-destructive hover:bg-muted flex items-center gap-2"
                            >
                              <Trash2 className="h-4 w-4" />
                              {t('common.delete')}
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Image Header */}
                  <div className="relative h-28 bg-input flex items-center justify-center overflow-hidden border-b border-border">
                    {recipe.image_url ? (
                      <img
                        src={recipe.image_url}
                        alt={recipe.name_en}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full bg-card flex items-center justify-center">
                        <Utensils className="h-16 w-16 text-border" />
                      </div>
                    )}
                    
                    {/* Recipe ID Badge */}
                    <div className="absolute top-3 left-3 px-2 py-1 rounded-full text-xs font-medium text-muted-foreground bg-muted/50 dark:bg-[rgba(10,18,25,0.5)]">
                      {recipe.id}
                    </div>
                    
                    {/* Pricing Indicator */}
                    {recipe.has_pricing !== false && (
                      <div className="absolute bottom-3 right-3 px-2 py-1 rounded-full text-xs font-medium text-orange-600 flex items-center gap-1">
                        🅿️
                      </div>
                    )}
                    
                    {/* View Details Overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all duration-300 flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <div className="bg-card px-4 py-2 rounded-lg text-sm font-medium text-card-foreground">
                        {t('common.clickToViewDetails')}
                      </div>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-3 space-y-2 flex-1">
                    {/* Recipe Names */}
                    <div className="text-center space-y-1 -mt-2 border-b border-border pb-2 -mx-3 px-3">
                      <h3 className="font-bold text-card-foreground text-lg group-hover:text-primary transition-colors truncate" title={recipe.name_en}>
                        {recipe.name_en}
                      </h3>
                      {recipe.name_ar && (
                        <p className="text-sm text-muted-foreground font-arabic truncate" title={recipe.name_ar}>
                          {recipe.name_ar}
                        </p>
                      )}
                    </div>

                    {/* Allergen Section */}
                    <div className="h-[48px] flex justify-center items-center">
                      {recipe.computed_allergens && recipe.computed_allergens.length > 0 ? (
                        <AllergenBadge
                          allergens={recipe.computed_allergens}
                          variant="warning"
                          size="xs"
                          showCount={true}
                          maxDisplay={2}
                        />
                      ) : (
                        <div className="text-xs text-muted-foreground">{t('common.noAllergens')}</div>
                      )}
                    </div>
                    
                    {/* Servings Info */}
                    <div className="space-y-1">
                      <div className="flex items-center justify-center">
                        <span className="text-sm text-card-foreground">
                          {recipe.servings || 0} {t('recipes.servings')}
                        </span>
                      </div>
                    </div>
                    
                    {/* Cost Information */}
                    <div className="space-y-1">
                      {/* Total Cost */}
                      <div className="p-2 text-center">
                        <div className="mb-1">
                          <span className="text-xs text-muted-foreground font-medium uppercase tracking-wide">{t('recipes.totalDishCost')}</span>
                        </div>
                        <div className="text-sm font-bold text-card-foreground">
                          {formatCurrency(recipe.total_cost || 0)}
                        </div>
                      </div>
                      
                      {/* Cost Per Serving */}
                      <div className="p-2 text-center -mx-3 bg-[#B3E8F2] dark:bg-[#2A4A5A]">
                        <div className="mb-1">
                          <span className="text-xs text-card-foreground font-medium uppercase tracking-wide">{t('recipes.costPerServing')}</span>
                        </div>
                        <div className="text-sm font-bold text-card-foreground">
                          {formatCurrency(recipe.cost_per_serving || 0)}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="px-3 pb-3 mt-auto">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/dish-costing/recipes/category/${recipe.category_id}`);
                      }}
                      className="w-full px-3 py-2 text-sm font-medium text-muted-foreground bg-muted/50 dark:bg-[rgba(10,18,25,0.5)] hover:bg-muted dark:hover:bg-muted border border-border hover:border-border rounded-md transition-all duration-200 flex items-center justify-center"
                      title={`${t('common.goTo')} ${i18n.language === 'ar' 
                        ? (categories.find(cat => cat.id === recipe.category_id)?.name_ar || categories.find(cat => cat.id === recipe.category_id)?.name_en || t('common.category'))
                        : (categories.find(cat => cat.id === recipe.category_id)?.name_en || t('common.category'))
                      }`}
                    >
                      <Folder className="h-4 w-4 mr-2" />
                      <span className={`truncate ${i18n.language === 'ar' ? 'font-arabic' : ''}`}>
                        {i18n.language === 'ar' 
                          ? (categories.find(cat => cat.id === recipe.category_id)?.name_ar || categories.find(cat => cat.id === recipe.category_id)?.name_en || t('common.category'))
                          : (categories.find(cat => cat.id === recipe.category_id)?.name_en || t('common.category'))
                        }
                      </span>
                    </button>
                  </div>
                </div>
              </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Empty State */}
      {filteredCategories.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
            {searchTerm ? <Search className="h-8 w-8 text-muted-foreground" /> : <Utensils className="h-8 w-8 text-muted-foreground" />}
          </div>
          <h3 className="text-lg font-medium text-foreground mb-2">
            {searchTerm ? t('common.noCategoriesFound') : t('common.noCategoriesYet')}
          </h3>
          <p className="text-muted-foreground mb-6">
            {searchTerm 
              ? t('common.tryAdjustingSearch') 
              : t('common.createFirstCategory')
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowCategoryForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('common.createFirstCategory')}
            </button>
          )}
        </div>
      )}

      {/* Category Form Modal */}
      {showCategoryForm && (
        <RecipeCategoryForm
          category={editingCategory}
          onSave={handleSaveCategory}
          onCancel={() => {
            setShowCategoryForm(false);
            setEditingCategory(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && categoryToDelete && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md mx-auto p-6">
            <div className="flex items-center mb-6">
              <AlertCircle className="h-6 w-6 text-destructive mr-3" />
              <h2 className="text-lg font-semibold text-card-foreground">{t('common.deleteCategory')}</h2>
            </div>
            <div className="mb-8">
              <p className="text-card-foreground">
                {t('common.confirmDeleteCategory', { name: categoryToDelete.name_en })}
              </p>
              {categoryToDelete.recipe_count > 0 && (
                <div className="mt-4 p-4 bg-chart-4/10 border border-chart-4/20 rounded-md">
                  <p className="text-sm text-chart-4">
                    ⚠️ This category contains {categoryToDelete.recipe_count} recipe(s). 
                    Deleting it will also remove all associated recipes.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setCategoryToDelete(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={confirmDeleteCategory}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
              >
                {t('common.deleteCategory')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Delete Confirmation Modal */}
      {showRecipeDeleteModal && recipeToDelete && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md mx-auto p-6">
            <div className="flex items-center mb-6">
              <AlertCircle className="h-6 w-6 text-destructive mr-3" />
              <h2 className="text-lg font-semibold text-card-foreground">{t('common.deleteRecipe')}</h2>
            </div>
            <div className="mb-8">
              <p className="text-card-foreground">
                {t('common.confirmDeleteRecipe', { name: recipeToDelete.name_en })}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowRecipeDeleteModal(false);
                  setRecipeToDelete(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={async () => {
                  try {
                    // Fetch full recipe data (including procedures) before deletion
                    console.log('🔍 Fetching full recipe data for cleanup before deletion...');
                    const fullRecipeData = await recipesAPI.getById(recipeToDelete.id);
                    console.log('📋 Full recipe data fetched:', fullRecipeData);
                    
                    await recipesAPI.delete(recipeToDelete.id);
                    
                    // Reload all recipes to ensure dependent recipes are updated
                    // This is necessary because deleting a recipe can affect other recipes that use it
                    await loadData();
                    
                    setSuccessMessage(`${t('recipes.recipeDeletedSuccessfully')}\n\n${t('recipes.allDependentRecipesRecalculated')}`);
                    setShowSuccessModal(true);
                    
                    // Clean up images in the background
                    if (fullRecipeData) {
                      // Clean up main recipe image
                      if (fullRecipeData.image_url) {
                        cleanupRecipeImage(fullRecipeData.image_url).catch(error => {
                          console.error('Background cleanup failed for recipe image:', error);
                        });
                      }
                      
                      // Clean up procedure step images
                      if (fullRecipeData.procedures && Array.isArray(fullRecipeData.procedures)) {
                        cleanupProcedureImages(fullRecipeData.procedures).catch(error => {
                          console.error('Background cleanup failed for procedure images:', error);
                        });
                      }
                    }
                  } catch (error) {
                    // Use the specific error message from the API if available
                    if (error.message && error.message.includes('Cannot delete recipe that is being used as ingredient in other recipes')) {
                      // Don't log expected constraint violations - they're handled gracefully
                      setErrorMessage(error.message);
                    } else {
                      // Log unexpected errors
                      console.error('Error deleting recipe:', error);
                      setErrorMessage(t('messages.failedDeleteRecipe'));
                    }
                    setShowErrorModal(true);
                  }
                  setShowRecipeDeleteModal(false);
                  setRecipeToDelete(null);
                }}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
              >
                {t('common.deleteRecipe')}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Category Bulk Delete Confirmation */}
      {showCategoryBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md mx-auto p-6">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-card-foreground mb-3">{t('common.confirmBulkDelete')}</h2>
              <p className="text-card-foreground">
                {t('common.confirmDeleteSelectedCategories', { count: selectedCategories.size })}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowCategoryBulkDeleteConfirm(false)} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">{t('common.cancel')}</button>
              <button onClick={async () => { setShowCategoryBulkDeleteConfirm(false); if (confirmAction) await confirmAction(); }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">{t('common.deleteSelectedCategoriesCount', { count: selectedCategories.size })}</button>
            </div>
          </div>
        </div>
      )}
      {/* Recipes Bulk Delete Confirmation */}
      {showBulkDeleteConfirm && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md mx-auto p-6">
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-card-foreground mb-3">{t('common.confirmBulkDelete')}</h2>
              <p className="text-card-foreground">
                {t('common.confirmDeleteSelectedRecipes', { count: selectedRecipes.size })}
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button onClick={() => setShowBulkDeleteConfirm(false)} className="inline-flex items-center px-4 py-2 border border-border rounded-lg text-sm font-medium text-card-foreground bg-background hover:bg-muted focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">{t('common.cancel')}</button>
              <button onClick={async () => { setShowBulkDeleteConfirm(false); if (confirmAction) await confirmAction(); }} className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-destructive-foreground bg-destructive hover:bg-destructive/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors">{t('common.delete')}</button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Details Modal */}
      {showRecipeDetails && selectedRecipe && (
        <RecipeDetails
          recipe={selectedRecipe}
          onClose={() => { setShowRecipeDetails(false); setSelectedRecipe(null); }}
        />
      )}

      {/* Recipe Form Modal */}
      {showRecipeForm && (
        <RecipeForm
          recipe={selectedRecipe}
          categories={categories}
          onSave={handleSaveRecipe}
          onCancel={() => {
            setShowRecipeForm(false);
            setSelectedRecipe(null);
          }}
        />
      )}

      {/* Error / success after recipe form so they stack above it (same z-index + DOM order) */}
      {showErrorModal && (
        <div className="modal-overlay modal-overlay-front">
          <div className="modal-content max-w-md mx-auto p-6">
            <div className="flex items-center mb-6">
              <AlertCircle className="h-6 w-6 text-destructive mr-3" />
              <h2 className="text-lg font-semibold text-card-foreground">{t('common.error')}</h2>
            </div>
            <div className="mb-8">
              <div className="text-card-foreground whitespace-pre-line">{errorMessage}</div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal && (
        <div className="modal-overlay modal-overlay-front">
          <div className="modal-content max-w-md mx-auto p-6">
            <div className="flex items-center mb-6">
              <CheckCircle className="h-6 w-6 text-chart-3 mr-3" />
              <h2 className="text-lg font-semibold text-card-foreground">{t('common.success')}</h2>
            </div>
            <div className="mb-8">
              <div className="text-card-foreground whitespace-pre-line">{successMessage}</div>
            </div>
            <div className="flex justify-end">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ring transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      </div>
    </div>
  );
};

export default RecipeCategories; 